import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { DownloadSimple, Eye, CheckCircle, XCircle } from '@phosphor-icons/react';

export default function BroadcastReportsPage() {
  const { selectedBot } = useOutletContext();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailResults, setDetailResults] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    const params = {};
    if (selectedBot) params.bot_id = selectedBot;
    API.get('/broadcast/reports', { params }).then(r => setReports(r.data.reports || [])).catch(() => {}).finally(() => setLoading(false));
  }, [selectedBot]);

  const viewDetail = async (report) => {
    try {
      const { data } = await API.get(`/broadcast/reports/${report.id}`);
      setDetail(data.broadcast);
      setDetailResults(data.results || []);
      setDetailOpen(true);
    } catch { toast.error('Failed to load details'); }
  };

  const downloadCsv = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/broadcast/reports/${id}/csv`, '_blank');
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="broadcast-reports-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Broadcast Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">{reports.length} broadcast{reports.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Sent By</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Total</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Success</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Failed</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Clicks</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r, i) => (
              <tr key={r.id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.created_at?.replace('T', ' ').slice(0, 19)}</td>
                <td className="px-4 py-3">
                  <span className="text-sm">{r.sent_by_name}</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">{r.sent_by_role}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={r.status === 'completed' ? 'default' : r.status === 'in_progress' ? 'secondary' : 'destructive'} className="text-xs">
                    {r.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.total_users}</td>
                <td className="px-4 py-3 text-right font-mono text-green-600">{r.success_count}</td>
                <td className="px-4 py-3 text-right font-mono text-red-600">{r.fail_count}</td>
                <td className="px-4 py-3 text-right font-mono text-blue-600" data-testid={`report-clicks-${r.id}`}>
                  {(r.button_clicks || []).reduce((s, b) => s + (b.clicks || 0), 0)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`view-report-${r.id}`} onClick={() => viewDetail(r)} className="p-1.5 rounded hover:bg-accent transition-colors"><Eye size={16} /></button>
                    <button data-testid={`download-report-${r.id}`} onClick={() => downloadCsv(r.id)} className="p-1.5 rounded hover:bg-accent transition-colors"><DownloadSimple size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {reports.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No broadcasts yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Broadcast Detail</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Sent By:</span> {detail.sent_by_name}</div>
                <div><span className="text-muted-foreground">Role:</span> {detail.sent_by_role}</div>
                <div><span className="text-muted-foreground">Total:</span> <span className="font-mono">{detail.total_users}</span></div>
                <div><span className="text-muted-foreground">Status:</span> {detail.status}</div>
              </div>
              {detail.text_1 && <div className="border border-border rounded p-3 text-sm"><strong>Text 1:</strong> {detail.text_1}</div>}
              {detail.text_2 && <div className="border border-border rounded p-3 text-sm"><strong>Text 2:</strong> {detail.text_2}</div>}
              {detail.button_clicks?.length > 0 && (
                <div className="border border-border rounded p-3 space-y-2" data-testid="button-clicks-analytics">
                  <p className="text-sm font-semibold">🔗 Button Click Analytics</p>
                  {detail.button_clicks.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{b.title}</span>
                        <span className="text-xs text-muted-foreground ml-2 truncate">{b.url}</span>
                      </div>
                      <Badge variant="secondary" className="ml-2 font-mono">{b.clicks} click{b.clicks !== 1 ? 's' : ''}</Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="border border-border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-3 py-2">User ID</th>
                      <th className="text-left px-3 py-2">Username</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailResults.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-b border-border">
                        <td className="px-3 py-2 font-mono">{r.telegram_user_id}</td>
                        <td className="px-3 py-2">{r.username}</td>
                        <td className="px-3 py-2">
                          {r.status === 'success' ? <CheckCircle size={14} className="text-green-500 inline" /> : <XCircle size={14} className="text-red-500 inline" />}
                          <span className="ml-1">{r.status}</span>
                        </td>
                        <td className="px-3 py-2 font-mono">{r.sent_at?.replace('T', ' ').slice(0, 19)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {detailResults.length > 100 && <p className="text-center text-xs text-muted-foreground py-2">Showing first 100 of {detailResults.length}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
